/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-tagless-components, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { modifier } from 'ember-modifier';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { filterBy, mapBy } from '@linc-technologies/linced-common/utils/arrays';
import { loadPeople } from '@linc-technologies/linced-common/utils/load-people';
import { restartableTask } from 'ember-concurrency';

// SIBLING_LISTS are the learner lists that the template shows.
const SIBLING_LISTS = [
  'allSiblings',
  'currentSiblings',
  'preEnrolledSiblings',
  'formerSiblings',
];

export default class SiblingManager extends Component {
  @service
  session;

  @service
  store;

  @service
  students;

  @service
  toaster;

  readOnly = false;

  @tracked
  siblingsDialogOpen = false;

  @tracked
  newSiblingDialogOpen = false;

  emptyArray = [];

  // loadOnInsert loads the siblings when the component is in the DOM, and
  // again when the person changes.
  loadOnInsert = modifier((_element, [person]) => {
    if (person) {
      this.loadSiblings.perform();
    }
  });

  // loadSiblings loads the siblings that the lists show. Former siblings are
  // also loaded.
  loadSiblings = restartableTask(async () => {
    await loadPeople(this.store, this.person.learner?.localSiblingIds ?? [], {
      includeFormer: true,
    });
    // The sibling lists read the people from the store, but they only
    // recompute when the siblings change. Make them recompute now. Do not
    // notify siblings: loadOnInsert reads it and starts this task again.
    for (const list of SIBLING_LISTS) {
      this.person.learner?.notifyPropertyChange(list);
    }
  });

  @computed('siblingsDialogOpen', 'students.all.[]')
  get allLearners() {
    // Trigger students service to load all students if not already primed.
    // This property will then recalculate once they're loaded.
    this.get('students.all');
    return this.store
      .peekAll('person')
      .filter((person) => person.type === PersonType.LEARNER);
  }

  @computed(
    'allLearners.@each.id',
    'person.{id,learner.siblings.@each.personId}',
    'session.activeSchool',
  )
  get learners() {
    let siblingIds = mapBy(
      filterBy(
        this.get('person.learner.siblings'),
        'schoolId',
        this.session.activeSchool,
      ),
      'personId',
    );
    return this.allLearners.filter((learner) => {
      // Remove the Learner in question and any existing siblings
      return !(
        siblingIds.includes(learner.id) || learner.id === this.person.id
      );
    });
  }

  @computed('person.fullName')
  get siblingsHeading() {
    return `Siblings: ${this.person.fullName}`;
  }

  @alias('person.learner.siblings')
  siblings;

  @tracked
  siblingCreateError;

  @tracked
  siblingDOB;

  @action
  setSiblingDOB(value) {
    this.siblingDOB = value;
  }

  @tracked
  siblingFirstName;

  @action
  setSiblingFirstName(value) {
    this.siblingFirstName = value;
  }

  @tracked
  siblingGender;

  @action
  setSiblingGender(value) {
    this.siblingGender = value;
  }

  @tracked
  siblingLastName;

  @action
  setSiblingLastName(value) {
    this.siblingLastName = value;
  }

  @tracked
  siblingSaving;

  @action
  toggleSiblingsDialog() {
    this.toggleProperty('siblingsDialogOpen');
    if (!this.siblingsDialogOpen) {
      // Closed without "updating" - revert any changes
      this.siblings.rollbackAttributes();
    }
  }

  @action
  addSibling(sibling) {
    this.siblings.createFragment({
      schoolId: this.session.activeSchool,
      personId: sibling.id,
    });
  }

  @action
  removeSibling(sibling) {
    this.siblings.removeObject(sibling);
  }

  @action
  updateSiblings() {
    // No action required - just close the dialog.  Record will be marked dirty; user can then save changes.
    this.siblingsDialogOpen = false;
  }

  @action
  toggleNewSiblingDialog() {
    this.toggleProperty('newSiblingDialogOpen');
    if (this.newSiblingDialogOpen) {
      // Reset new sibling form
      this.siblingFirstName = '';
      this.siblingLastName = this.get('person.lastName');
      this.siblingDOB = '';
      this.siblingGender = '';
      this.siblingSaving = false;
      this.siblingCreateError = false;
    } else {
      // Closed without "updating" - revert any changes
      this.siblings.rollbackAttributes();
    }
  }

  @action
  saveNewSibling() {
    this.siblingSaving = true;
    this.siblingCreateError = false;
    let sibling = this.store.createRecord('people/enrolment', {
      jurisdiction: this.person.jurisdiction,
      status: EnrolmentStatus.PRE_ENROLLED,
      fieldsArray: [],
      learner: {
        schoolId: this.session.activeSchool,
      },
    });

    let newFields = {
      'core:firstNameLegal': 'siblingFirstName',
      'core:lastNameLegal': 'siblingLastName',
      'core:firstNamePreferred': 'siblingFirstName',
      'core:lastNamePreferred': 'siblingLastName',
      'core:dob': 'siblingDOB',
    };
    if (!isEmpty(this.genderField)) {
      newFields[this.genderField.key] = 'siblingGender';
    }
    let keys = Object.keys(newFields);

    // Add fields and populate from either this Person or the field's default.
    this.groups.forEach((group) => {
      group.keyedFields.forEach((field) => {
        // Add field to the sibling
        sibling.addField(field.key, field.dataType);

        if (keys.includes(field.key)) {
          // This field was specified as part of the form.
          sibling.fields
            .get(field.key)
            .set('value', this.get(newFields[field.key]));
        } else if (field.isSiblingLinked) {
          // This field is sibling linked; populate it from the original Person.
          let value = this.person.get(`fields.${field.key}.value`);
          if (field.dataType === DataType.LIST) {
            // Value is a list, so needs to be copied rather than just mushed in.
            sibling.fields.get(field.key).set('value', value.copy());
          } else {
            sibling.fields.get(field.key).set('value', value);
          }
        } else if (!isEmpty(field.default) && !isEmpty(field.default.value)) {
          // This field isn't sibling linked, but the field has a default value.  Use that.
          if (field.dataType === DataType.LIST) {
            sibling.fields
              .get(field.key)
              .set('value', field.default.value.copy());
          } else {
            sibling.fields.get(field.key).set('value', field.default.value);
          }
        }
      });
    });

    // Add Contacts from this Person.
    let contacts = this.person.get('learner.contacts');
    if (!isEmpty(contacts) && contacts.length > 0) {
      contacts.forEach((contact) => {
        let contactCopy = contact.copy();
        contactCopy.set('allowedAccess', false);
        contactCopy.set('billPayer', false);
        sibling.learner.contacts.pushObject(contactCopy);
      });
    }

    // Add Siblings from this Person.  Only the ones at the active school are
    // copied; links to other schools belong to this Person's own enrolment
    // history and must not follow a brand-new Learner into this school.
    let siblings = this.person.get('learner.siblings');
    if (!isEmpty(siblings) && siblings.length > 0) {
      filterBy(siblings, 'schoolId', this.session.activeSchool).forEach(
        (sib) => {
          sibling.learner.siblings.pushObject(sib.copy());
        },
      );
    }

    // Add this Person as a Sibling.
    sibling.learner.siblings.createFragment({
      schoolId: this.get('session.activeSchool'),
      personId: this.person.id,
    });

    // Save the new Sibling.
    sibling
      .save()
      .then(
        () => {
          // Update this Person record to pull in the new Sibling, and load
          // the new Sibling in full: the save puts it in the store with no
          // learner data, so loadSiblings does not load it. Then make the
          // sibling lists recompute.
          return Promise.all([
            this.person.reload(),
            this.store.query('person', {
              includeFormer: true,
              people: JSON.stringify([sibling.personId]),
            }),
          ])
            .then(() => this.loadSiblings.perform())
            .catch((err) => {
              // The Sibling was created; only the refresh failed.  Don't ask
              // the user to re-check their details - re-submitting this form
              // would enrol a second Learner.
              console.error('Failed to refresh after enrolling sibling', err);
              this.toaster.error(
                'The sibling was enrolled, but this page could not be refreshed.  If you do not see them, please re-load the page.',
              );
            })
            .finally(() => {
              // The Sibling exists either way, so always close the dialog.
              this.newSiblingDialogOpen = false;
            });
        },
        (err) => {
          // The Sibling wasn't created, so the details entered may be at fault.
          console.error('Failed to save new sibling', err);
          this.siblingCreateError = true;
        },
      )
      .finally(() => {
        this.siblingSaving = false;
      });
  }
}
